import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

// The sticky line where the floating week header pins: the app header
// (--app-header-height, 5rem) plus the floating controls bar
// (--floating-controls-height, 3rem) in src/index.css.
const FLOATING_TOP_PX = 128;

interface StickyProjectHeaderContextValue {
  activeProjectId: string | null;
  register: (projectId: string, element: HTMLElement) => void;
  unregister: (projectId: string) => void;
}

const StickyProjectHeaderContext =
  createContext<StickyProjectHeaderContextValue | null>(null);

export function StickyProjectHeaderProvider({ children }: { children: ReactNode }) {
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  const sentinelsRef = useRef(new Map<string, HTMLElement>());
  const aboveRef = useRef(new Set<string>());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const elementToIdRef = useRef(new WeakMap<HTMLElement, string>());

  const recompute = useCallback(() => {
    let active: { id: string; top: number } | null = null;
    for (const id of aboveRef.current) {
      const el = sentinelsRef.current.get(id);
      if (!el) continue;
      const top = el.getBoundingClientRect().top;
      // Among above-the-line sentinels, the one with the LARGEST top is
      // closest to the line and was the most recently crossed = the one
      // that should currently be displayed as the sticky header.
      if (active === null || top > active.top) {
        active = { id, top };
      }
    }
    const next = active?.id ?? null;
    setActiveProjectId((prev) => (prev === next ? prev : next));
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const id = elementToIdRef.current.get(entry.target as HTMLElement);
          if (!id) continue;
          const above = entry.boundingClientRect.top < FLOATING_TOP_PX;
          if (above) aboveRef.current.add(id);
          else aboveRef.current.delete(id);
        }
        recompute();
      },
      {
        rootMargin: `-${FLOATING_TOP_PX}px 0px 0px 0px`,
        threshold: [0, 1],
      },
    );
    observerRef.current = observer;
    for (const el of sentinelsRef.current.values()) observer.observe(el);
    return () => {
      observer.disconnect();
      observerRef.current = null;
    };
  }, [recompute]);

  const register = useCallback(
    (projectId: string, element: HTMLElement) => {
      sentinelsRef.current.set(projectId, element);
      elementToIdRef.current.set(element, projectId);
      observerRef.current?.observe(element);
      const above = element.getBoundingClientRect().top < FLOATING_TOP_PX;
      if (above) aboveRef.current.add(projectId);
      else aboveRef.current.delete(projectId);
      recompute();
    },
    [recompute],
  );

  const unregister = useCallback(
    (projectId: string) => {
      const el = sentinelsRef.current.get(projectId);
      if (el) {
        observerRef.current?.unobserve(el);
        elementToIdRef.current.delete(el);
      }
      sentinelsRef.current.delete(projectId);
      aboveRef.current.delete(projectId);
      recompute();
    },
    [recompute],
  );

  const value = useMemo(
    () => ({ activeProjectId, register, unregister }),
    [activeProjectId, register, unregister],
  );

  return (
    <StickyProjectHeaderContext.Provider value={value}>
      {children}
    </StickyProjectHeaderContext.Provider>
  );
}

export function useStickyProjectHeader(): StickyProjectHeaderContextValue | null {
  return useContext(StickyProjectHeaderContext);
}
